import { rgba } from 'polished';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '~/components';
import { useWorkspace } from '~/contexts';
import { useAuth, useFeatures } from '~/hooks';
import { colors, weights } from '~/styles';
import AppVersion from './AppVersion';
import Maintenance from './Maintenance';
import SidebarAiCta from './SidebarAiCta';
import SidebarTrialWidget from './SidebarTrialWidget';

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
`;

const AreaButton = styled.button`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 2.6875rem;
  padding: 0;
  border: none;
  border-radius: 0;
  color: ${({ $isActive }) => ($isActive ? colors.primary : colors.grey40)};
  font-size: 0.875rem;
  background-color: ${({ $isActive }) => ($isActive ? rgba(colors.white, 0.08) : 'transparent')};
  cursor: pointer;
  z-index: 91;

  &:hover {
    color: ${({ $isActive }) => ($isActive ? colors.primary : colors.white)};
    background-color: ${({ $isActive }) => ($isActive ? rgba(colors.white, 0.08) : rgba(colors.white, 0.04))};
  }

  > svg {
    width: 1rem;
  }
`;

const AreaBadge = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  background-color: ${colors.danger};
  border-radius: 50%;
`;

const Popover = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  margin-left: 0.3125rem;
  padding: 0.9375rem;
  background-color: ${colors.white};
  border-radius: 0.3125rem;
  filter: drop-shadow(0 0 0.4375rem rgba(0, 0, 0, 0.25));
  z-index: 95;

  &::before {
    content: '';
    position: absolute;
    top: 0.75rem;
    left: -0.5rem;
    width: 0.8125rem;
    height: 1.3125rem;
    background: ${colors.white};
    clip-path: polygon(100% 0%, 0% 50%, 100% 100%);
  }
`;

const PopoverTitle = styled.div`
  color: ${colors.grey40};
  font-size: 0.625rem;
  font-weight: ${weights.black};
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
  margin-bottom: 0.9375rem;
`;

const PopoverLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
`;

const PopoverLink = styled(NavLink)`
  color: ${colors.black};
  font-size: 0.875rem;
  font-weight: ${weights.normal};
  line-height: 1;
  white-space: nowrap;

  &:hover {
    color: ${colors.primary};
  }

  &.is-active {
    font-weight: ${weights.bold};
    color: ${colors.primary};
  }
`;

const PopoverLinkBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  margin-left: 0.5rem;
  padding: 0 0.25rem;
  color: ${colors.white};
  font-size: 0.625rem;
  font-weight: ${weights.bold};
  background-color: ${colors.danger};
  border-radius: 999rem;
  vertical-align: middle;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
`;

const HoverLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 100%;
  padding: 0.625rem 0.5rem;
  background-color: ${colors.black};
  border-radius: 0 0.25rem 0.25rem 0;
  color: ${colors.white};
  font-size: 0.625rem;
  font-weight: ${weights.black};
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
  white-space: nowrap;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0;
  z-index: 95;
`;

const Area = styled.div`
  position: relative;

  &:hover ${HoverLabel} {
    opacity: 1;
  }
`;

const NavFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin-top: auto;
  z-index: 3;
  padding-top: 0.75rem;
  padding-bottom: 0.625rem;
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${colors.black};
  cursor: pointer;
  z-index: 2;
`;

function NavArea({ name, icon, path, areaId, navAreaId, badgeCount, openAreaId, setOpenAreaId, children }) {
  const match = useRouteMatch();
  const isActive = useMemo(() => areaId === navAreaId, [areaId, navAreaId]);
  const isOpen = openAreaId === areaId;
  const areaRef = useRef(null);

  const handleClose = useCallback(() => {
    setOpenAreaId(null);
  }, [setOpenAreaId]);

  // Close popover when navigating
  useEffect(() => {
    setOpenAreaId(null);
  }, [navAreaId, setOpenAreaId]);

  const handleButtonClick = useCallback(() => {
    if (children) {
      setOpenAreaId((prev) => (prev === areaId ? null : areaId));
    }
  }, [children, areaId, setOpenAreaId]);

  return (
    <Area data-area={areaId} ref={areaRef}>
      {children ? (
        <AreaButton $isActive={isActive} onClick={handleButtonClick}>
          <Icon icon={icon} />
          {badgeCount > 0 && <AreaBadge />}
        </AreaButton>
      ) : (
        <AreaButton as={NavLink} to={`${match.url}${path}`} $isActive={isActive}>
          <Icon icon={icon} />
          {badgeCount > 0 && <AreaBadge />}
        </AreaButton>
      )}
      {!isOpen && <HoverLabel>{name}</HoverLabel>}
      {isOpen && children && (
        <>
          <Overlay onClick={handleClose} />
          <Popover>
            <PopoverTitle>{name}</PopoverTitle>
            <PopoverLinks>{children}</PopoverLinks>
          </Popover>
        </>
      )}
    </Area>
  );
}

function NavItem({ name, path, badgeCount, ...props }) {
  const match = useRouteMatch();
  return (
    <PopoverLink to={`${match.url}${path}`} className="item-link" activeClassName="is-active" {...props}>
      <span>{name}</span>
      {badgeCount > 0 && <PopoverLinkBadge>{badgeCount < 100 ? badgeCount : '99+'}</PopoverLinkBadge>}
    </PopoverLink>
  );
}

export default function CollapsedMenu({ navAreaId, timeApprovalsCount, expenseApprovalsCount, onExpand }) {
  const auth = useAuth();
  const features = useFeatures();
  const { workspace } = useWorkspace();
  const isBillingLocked = !workspace.billingStatus?.isValid;
  const isDisabled = workspace.statusId === 'disabled';
  const [openAreaId, setOpenAreaId] = useState(null);

  const isTimeActive = (_, location) =>
    ['month', 'week', 'day', 'list'].some((v) => location.pathname.includes(`/time/${v}`));

  if (isBillingLocked || isDisabled) {
    return (
      <>
        <Container>
          <Nav>
            <NavArea
              name="Home"
              icon="home"
              path=""
              areaId="home"
              navAreaId={navAreaId}
              openAreaId={openAreaId}
              setOpenAreaId={setOpenAreaId}
            />
            {!isDisabled && auth.workspace.manage && (
              <NavArea
                name="Settings"
                icon="cog"
                path="/settings"
                areaId="settings"
                navAreaId={navAreaId}
                openAreaId={openAreaId}
                setOpenAreaId={setOpenAreaId}>
                <NavItem name="Billing" path="/settings/billing" />
              </NavArea>
            )}
          </Nav>
          <NavFooter>
            <AppVersion />
          </NavFooter>
        </Container>
        <Background onClick={onExpand} />
      </>
    );
  }

  return (
    <>
      <Container>
        <Nav>
          <NavArea
            name="Home"
            icon="home"
            path=""
            areaId="home"
            navAreaId={navAreaId}
            openAreaId={openAreaId}
            setOpenAreaId={setOpenAreaId}>
            <NavItem name="Dashboard" path="/dashboard" />
            <NavItem name="My Projects" path="/my-projects" />
            <NavItem name="My Tasks" path="/my-tasks" />
            {features.allocations && <NavItem name="My Allocations" path="/my-allocations" />}
          </NavArea>
          {features.pipeline && auth.pipeline.view && (
            <NavArea
              name="Pipeline"
              icon="filter-circle-dollar"
              path="/pipeline"
              areaId="pipeline"
              navAreaId={navAreaId}
              openAreaId={openAreaId}
              setOpenAreaId={setOpenAreaId}>
              {auth.opportunities.view && <NavItem name="Opportunities" path="/pipeline/opportunities" />}
              {auth.companies.view && <NavItem name="Companies" path="/pipeline/companies" />}
              {auth.contacts.view && <NavItem name="Contacts" path="/pipeline/contacts" />}
            </NavArea>
          )}
          <NavArea
            name="Portfolio"
            icon="tasks"
            path="/portfolio"
            areaId="portfolio"
            navAreaId={navAreaId}
            openAreaId={openAreaId}
            setOpenAreaId={setOpenAreaId}>
            <NavItem name="Projects" path="/portfolio/projects" />
            <NavItem name="Clients" path="/portfolio/clients" />
          </NavArea>
          {features.allocations && auth.allocations.view && (
            <NavArea
              name="Resources"
              icon="users"
              path="/resources"
              areaId="resources"
              navAreaId={navAreaId}
              openAreaId={openAreaId}
              setOpenAreaId={setOpenAreaId}>
              <NavItem name="Allocations" path="/resources/allocations" />
            </NavArea>
          )}
          <NavArea
            name="Time"
            icon="clock"
            path="/time"
            areaId="time"
            navAreaId={navAreaId}
            openAreaId={openAreaId}
            setOpenAreaId={setOpenAreaId}
            badgeCount={timeApprovalsCount}>
            <NavItem name="Time Entries" path="/time" isActive={isTimeActive} />
            {auth.time.approve && <NavItem name="Approvals" path="/time/approvals" badgeCount={timeApprovalsCount} />}
            <NavItem name="Auditing" path="/time/auditing" />
            {features.timesheets && <NavItem name="Timesheets" path="/time/timesheets" />}
          </NavArea>
          {features.expenseReporting && (
            <NavArea
              name="Expenses"
              icon="receipt"
              path="/expenses"
              areaId="expenses"
              navAreaId={navAreaId}
              openAreaId={openAreaId}
              setOpenAreaId={setOpenAreaId}
              badgeCount={expenseApprovalsCount}>
              <NavItem name="Expense Reports" path="/expenses/reports" />
              {auth.expenses.approve && (
                <NavItem name="Approvals" path="/expenses/approvals" badgeCount={expenseApprovalsCount} />
              )}
              <NavItem name="Auditing" path="/expenses/auditing" />
            </NavArea>
          )}
          {auth.invoices.view.project && (
            <NavArea
              name="Billing"
              icon="file-invoice"
              path="/billing"
              areaId="billing"
              navAreaId={navAreaId}
              openAreaId={openAreaId}
              setOpenAreaId={setOpenAreaId}>
              {auth.invoices.manage.project && <NavItem name="Ready to Bill" path="/billing/ready-to-bill" />}
              <NavItem name="Invoices" path="/billing/invoices" />
              {auth.payments.view && <NavItem name="Payments" path="/billing/payments" />}
              {auth.creditNotes.view && <NavItem name="Credit Notes" path="/billing/credit-notes" />}
            </NavArea>
          )}
          <NavArea
            name="Reports"
            icon="chart-line"
            path="/reports"
            areaId="reports"
            navAreaId={navAreaId}
            openAreaId={openAreaId}
            setOpenAreaId={setOpenAreaId}>
            <NavItem name="My Reports" path="/reports/my-reports" />
            <NavItem name="Library" path="/reports/library" />
            {features.reportSchedules && <NavItem name="Schedules" path="/reports/schedules" />}
            <NavItem name="Exports" path="/reports/exports" />
          </NavArea>
          {auth.workspace.manage && (
            <NavArea
              name="Settings"
              icon="cog"
              path="/settings"
              areaId="settings"
              navAreaId={navAreaId}
              openAreaId={openAreaId}
              setOpenAreaId={setOpenAreaId}>
              <NavItem name="Workspace" path="/settings" exact />
              <NavItem name="Invoices" path="/settings/invoices" />
              <NavItem name="Members" path="/settings/members" />
              <NavItem name="Security Roles" path="/settings/security-roles" />
              <NavItem name="Custom Data" path="/settings/custom-data" />
              <NavItem name="Features" path="/settings/features" />
              <NavItem name="Integrations" path="/settings/integrations" />
              <NavItem name="API Keys" path="/settings/api-keys" />
              <NavItem name="Authentication" path="/settings/authentication" />
              <NavItem name="Billing" path="/settings/billing" />
            </NavArea>
          )}
        </Nav>
        <NavFooter>
          <AppVersion collapsed />
          <SidebarTrialWidget collapsed />
          <Maintenance collapsed />
          <SidebarAiCta collapsed />
        </NavFooter>
      </Container>
      <Background onClick={onExpand} />
    </>
  );
}
